import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { archivedBooks, bookLabel, type Book } from "../data/books.ts";
import PuzzleHelp from "./PuzzleHelp.tsx";
import { Button, Note } from "./ui/index.ts";

/**
 * Picking the puzzle you are stuck on.
 *
 * A reader reaches this by scanning the code in the front of a book, so they
 * arrive on a phone, mid-solve, with the book open at a numbered page. The
 * whole interaction is: which book, which number, go. `?book=` is prefilled
 * from the code itself.
 *
 * Only archived books can appear. The site must never regenerate a puzzle from
 * its seed — seeds reproduce a book only against the generator that made it —
 * so a book with no snapshot has nothing trustworthy to show, and the form says
 * so instead of offering a control that cannot work.
 */
export default function HelpForm({ book }: { book?: Book }) {
  const [params, setParams] = useSearchParams();
  const ready = archivedBooks();
  const [slug, setSlug] = useState(book?.slug ?? params.get("book") ?? ready[0]?.slug ?? "");
  const [number, setNumber] = useState(params.get("puzzle") ?? "");

  if (ready.length === 0) {
    return (
      <Note>
        <p className="mb-[1em]">
          <strong>Not open yet.</strong> This needs each published book's puzzles archived here
          first, so the puzzle on your screen is exactly the one printed on your page.
        </p>
        <p className="mb-0">
          Until then: every book carries a complete answer key in the back, and a step-by-step guide
          to the techniques in the front.
        </p>
      </Note>
    );
  }

  const canStart = slug !== "" && Number(number) > 0;

  /*
   * The puzzle being helped with is whatever is in the URL, not whatever the
   * controls currently show — so editing the form does not swap the grid out
   * from under someone until they press Start, and so the address a QR code
   * points at is the whole state. A number that is not a positive integer is
   * treated as no selection rather than as an error: it can only get there by
   * being typed or pasted.
   */
  const active = book ?? ready.find((b) => b.slug === params.get("book"));
  const activeNumber = Number(params.get("puzzle"));
  const showPuzzle = active !== undefined && Number.isInteger(activeNumber) && activeNumber > 0;

  return (
    <>
      <form
        className="flex gap-[0.8rem] items-end flex-wrap mt-[1.6rem] p-[1.3rem] bg-panel grain border border-line rounded-box max-w-[44rem]"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(book ? { puzzle: number } : { book: slug, puzzle: number });
        }}
      >
        {/*
          When the address already names the book — which it does for anyone
          who got here by scanning the code in one — there is nothing to pick,
          so the control becomes a label. Asking a reader which book they are
          holding when we were told is one question too many on a phone,
          mid-solve.

          A book title is a sentence and a puzzle number is two digits, so when
          there is a choice to make the two get very different room. They used
          to share a basis.
        */}
        {book ? (
          <div className="flex-[3_1_18rem] min-w-0">
            <p className="block font-bold text-[0.7rem] uppercase tracking-[0.13em] text-muted mb-[0.35rem]">
              Your book
            </p>
            <p className="m-0 font-display font-bold text-[1.05rem] leading-[1.3]">
              {bookLabel(book)}
            </p>
          </div>
        ) : (
          <div className="flex-[3_1_18rem] min-w-0">
            <label
              className="block font-bold text-[0.7rem] uppercase tracking-[0.13em] text-muted mb-[0.35rem]"
              htmlFor="book"
            >
              Which book
            </label>
            <select id="book" value={slug} onChange={(e) => setSlug(e.target.value)}>
              {ready.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {bookLabel(b)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-[1_1_7rem] min-w-0">
          <label
            className="block font-bold text-[0.7rem] uppercase tracking-[0.13em] text-muted mb-[0.35rem]"
            htmlFor="number"
          >
            Puzzle number
          </label>
          <input
            id="number"
            type="number"
            inputMode="numeric"
            min={1}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="37"
          />
        </div>

        <Button disabled={!canStart} type="submit">
          Start
        </Button>
      </form>

      {showPuzzle ? <PuzzleHelp slug={active.slug} number={activeNumber} /> : null}
    </>
  );
}
